import { useState } from 'react'
import { isAxiosError } from 'axios'
import { Modal } from '@/components/ui/Modal'
import { PhraseForm } from './PhraseForm'
import type { PhraseValues } from './PhraseForm'
import { updateCustomFlashcard, deleteCustomFlashcard } from '@/api/customFlashcards'
import { cn } from '@/utils/cn'
import type { CustomVocabularyEntry } from '@/types/vocabulary'

interface EditPhraseModalProps {
  entry: CustomVocabularyEntry
  languageId: number
  onClose: () => void
  onSuccess: () => void
}

export function EditPhraseModal({ entry, languageId, onClose, onSuccess }: EditPhraseModalProps) {
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  function handleUpdate(values: PhraseValues) {
    setSubmitting(true)
    setErrors({})
    updateCustomFlashcard(entry.custom_flashcard_id, languageId, {
      lemma: values.phrase,
      translation: values.translation,
      notes: values.notes || null,
      deck_id: values.deck_id,
    })
      .then(() => {
        onSuccess()
        onClose()
      })
      .catch((err: unknown) => {
        if (isAxiosError(err) && err.response?.data?.errors) {
          setErrors(err.response.data.errors as Record<string, string[]>)
        } else {
          setErrors({ base: ['Something went wrong. Please try again.'] })
        }
      })
      .finally(() => setSubmitting(false))
  }

  function handleDelete() {
    if (!confirmingDelete) {
      setConfirmingDelete(true)
      return
    }
    setSubmitting(true)
    deleteCustomFlashcard(entry.custom_flashcard_id, languageId)
      .then(() => {
        onSuccess()
        onClose()
      })
      .catch(() => setErrors({ base: ['Could not delete the phrase. Please try again.'] }))
      .finally(() => setSubmitting(false))
  }

  return (
    <Modal open onClose={onClose} title="Edit phrase">
      <PhraseForm
        languageId={languageId}
        initial={{
          phrase: entry.lemma,
          translation: entry.translations[0] ?? '',
          notes: entry.notes ?? '',
          deck_id: entry.deck_id,
        }}
        submitLabel="Save"
        submitting={submitting}
        errors={errors}
        onSubmit={handleUpdate}
      />
      <button
        type="button"
        onClick={handleDelete}
        disabled={submitting}
        className={cn(
          'mt-3 px-4 py-2 rounded-full text-xs font-light min-h-11',
          'border border-error/40 text-error hover:bg-error/5',
          'transition-colors duration-200 disabled:opacity-50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
        )}
      >
        {confirmingDelete ? 'Confirm delete' : 'Delete'}
      </button>
    </Modal>
  )
}
